import React, { useState } from 'react';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  XMarkIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  TagIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const SearchFilters = ({ onSearch, onFiltersChange, countries, currencies }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    stage: '',
    country: '',
    currency: '',
    minFunding: '',
    maxFunding: '',
    minROI: '',
    maxROI: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const categories = [
    'Technologie',
    'Santé',
    'Éducation',
    'Finance',
    'E-commerce',
    'Environnement',
    'Transport',
    'Immobilier',
    'Agriculture',
    'Tourisme',
    'Autre'
  ];

  const stages = [
    'Idée',
    'Prototype',
    'MVP',
    'Croissance',
    'Expansion',
    'Maturité'
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    onSearch(searchTerm);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      category: '',
      stage: '',
      country: '',
      currency: '',
      minFunding: '',
      maxFunding: '',
      minROI: '',
      maxROI: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters).some(value => 
    value !== '' && value !== 'createdAt' && value !== 'desc'
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      {/* Barre de recherche principale */}
      <form onSubmit={handleSearch} className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher des projets, idées, entrepreneurs..."
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
          <button
            type="submit"
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <span className="sr-only">Rechercher</span>
          </button>
        </div>
      </form>

      {/* Bouton filtres */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <FunnelIcon className="h-4 w-4 mr-2" />
          Filtres avancés
          {hasActiveFilters && (
            <span className="ml-2 px-2 py-1 text-xs bg-primary-500 text-white rounded-full">
              {Object.values(filters).filter(v => v !== '' && v !== 'createdAt' && v !== 'desc').length}
            </span>
          )}
        </button>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            Effacer les filtres
          </button>
        )}
      </div>

      {/* Panneau de filtres */}
      {showFilters && (
        <div className="border-t border-gray-200 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <TagIcon className="h-4 w-4 inline mr-1" />
                Catégorie
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">Toutes les catégories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Stade de développement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Stade
              </label>
              <select
                value={filters.stage}
                onChange={(e) => handleFilterChange('stage', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">Tous les stades</option>
                {stages.map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
            </div>

            {/* Pays */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPinIcon className="h-4 w-4 inline mr-1" />
                Pays
              </label>
              <select
                value={filters.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">Tous les pays</option>
                {countries?.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Devise */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CurrencyDollarIcon className="h-4 w-4 inline mr-1" />
                Devise
              </label>
              <select
                value={filters.currency}
                onChange={(e) => handleFilterChange('currency', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">Toutes les devises</option>
                {currencies?.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.symbol} {currency.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Financement minimum */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Financement min
              </label>
              <input
                type="number"
                value={filters.minFunding}
                onChange={(e) => handleFilterChange('minFunding', e.target.value)}
                placeholder="0"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>

            {/* Financement maximum */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Financement max
              </label>
              <input
                type="number"
                value={filters.maxFunding}
                onChange={(e) => handleFilterChange('maxFunding', e.target.value)}
                placeholder="∞"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>

            {/* ROI minimum */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ROI min (%)
              </label>
              <input
                type="number"
                value={filters.minROI}
                onChange={(e) => handleFilterChange('minROI', e.target.value)}
                placeholder="0"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>

            {/* ROI maximum */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ROI max (%)
              </label>
              <input
                type="number"
                value={filters.maxROI}
                onChange={(e) => handleFilterChange('maxROI', e.target.value)}
                placeholder="∞"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>

            {/* Tri */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trier par
              </label>
              <select
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-');
                  handleFilterChange('sortBy', sortBy);
                  handleFilterChange('sortOrder', sortOrder);
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="createdAt-desc">Plus récents</option>
                <option value="createdAt-asc">Plus anciens</option>
                <option value="title-asc">Titre A-Z</option>
                <option value="title-desc">Titre Z-A</option>
                <option value="fundingNeeded-asc">Financement croissant</option>
                <option value="fundingNeeded-desc">Financement décroissant</option>
                <option value="expectedROI-desc">ROI décroissant</option>
                <option value="expectedROI-asc">ROI croissant</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;

